import React, { useState, useEffect } from 'react';
import { Bell, Shield, Database, Eye } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import './Settings.css';

const Settings = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [settings, setSettings] = useState({
    // Notifications
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    patientAlerts: true,
    systemUpdates: false,
    
    // Appearance (darkMode synced with theme context)
    darkMode: false,
    language: 'en',
    timezone: 'America/Los_Angeles',
    
    // Privacy & Security
    twoFactorAuth: false,
    sessionTimeout: '30',
    dataSharing: false,
    
    // Data & Storage
    autoBackup: true,
    dataRetention: '1year',
    exportFormat: 'csv'
  });

  useEffect(() => {
    setSettings(prev => ({ ...prev, darkMode: isDarkMode }));
  }, [isDarkMode]);

  const handleToggle = (setting) => {
    if (setting === 'darkMode') {
      toggleTheme();
      return;
    }
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSelectChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Customize your application preferences and security settings</p>
      </div>

      <div className="settings-content">
        {/* Notifications Section */}
        <div className="settings-section">
          <div className="section-header">
            <Bell size={20} />
            <h2>Notifications</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Email Notifications</span>
                <span className="setting-description">Receive updates and alerts via email</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={() => handleToggle('emailNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">SMS Notifications</span>
                <span className="setting-description">Get critical alerts via text message</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.smsNotifications}
                  onChange={() => handleToggle('smsNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Push Notifications</span>
                <span className="setting-description">Browser notifications for real-time updates</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.pushNotifications}
                  onChange={() => handleToggle('pushNotifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Patient Alerts</span>
                <span className="setting-description">Notifications for patient medication events</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.patientAlerts}
                  onChange={() => handleToggle('patientAlerts')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">System Updates</span>
                <span className="setting-description">Notifications about system maintenance and updates</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.systemUpdates}
                  onChange={() => handleToggle('systemUpdates')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Appearance Section */}
        <div className="settings-section">
          <div className="section-header">
            <Eye size={20} />
            <h2>Appearance</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Dark Mode</span>
                <span className="setting-description">Switch to dark theme for better visibility</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.darkMode}
                  onChange={() => handleToggle('darkMode')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Language</span>
                <span className="setting-description">Choose your preferred language</span>
              </div>
              <select
                value={settings.language}
                onChange={(e) => handleSelectChange('language', e.target.value)}
                className="setting-select"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Timezone</span>
                <span className="setting-description">Select your local timezone</span>
              </div>
              <select
                value={settings.timezone}
                onChange={(e) => handleSelectChange('timezone', e.target.value)}
                className="setting-select"
              >
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/New_York">Eastern Time (ET)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Privacy & Security Section */}
        <div className="settings-section">
          <div className="section-header">
            <Shield size={20} />
            <h2>Privacy & Security</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Two-Factor Authentication</span>
                <span className="setting-description">Add an extra layer of security to your account</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.twoFactorAuth}
                  onChange={() => handleToggle('twoFactorAuth')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Session Timeout</span>
                <span className="setting-description">Automatically log out after inactivity</span>
              </div>
              <select
                value={settings.sessionTimeout}
                onChange={(e) => handleSelectChange('sessionTimeout', e.target.value)}
                className="setting-select"
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="240">4 hours</option>
                <option value="never">Never</option>
              </select>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Data Sharing</span>
                <span className="setting-description">Allow anonymized data sharing for research</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.dataSharing}
                  onChange={() => handleToggle('dataSharing')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Data & Storage Section */}
        <div className="settings-section">
          <div className="section-header">
            <Database size={20} />
            <h2>Data & Storage</h2>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Automatic Backup</span>
                <span className="setting-description">Automatically backup your data daily</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.autoBackup}
                  onChange={() => handleToggle('autoBackup')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Data Retention</span>
                <span className="setting-description">How long to keep your data</span>
              </div>
              <select
                value={settings.dataRetention}
                onChange={(e) => handleSelectChange('dataRetention', e.target.value)}
                className="setting-select"
              >
                <option value="6months">6 months</option>
                <option value="1year">1 year</option>
                <option value="2years">2 years</option>
                <option value="5years">5 years</option>
                <option value="forever">Forever</option>
              </select>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-title">Export Format</span>
                <span className="setting-description">Default format for data exports</span>
              </div>
              <select
                value={settings.exportFormat}
                onChange={(e) => handleSelectChange('exportFormat', e.target.value)}
                className="setting-select"
              >
                <option value="csv">CSV</option>
                <option value="json">JSON</option>
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-footer">
        <button className="save-settings-btn">Save All Settings</button>
        <button className="reset-settings-btn">Reset to Defaults</button>
      </div>
    </div>
  );
};

export default Settings;

